import { toSafeString } from './toSafeString.js';
import { isResultLikeValue } from './isResultLikeValue.js';
import { resolvePieveToolCallDisplayIntentText, toPastTenseDisplayLabel } from './pieveToolCallDisplayIntent.js';
import { resolvePieveMcpToolCallDisplayText } from './resolvePieveMcpToolCallDisplayText.js';
import resolvePieveKnowledgeToolCallDisplayText from './resolvePieveKnowledgeToolCallDisplayText.js';
import resolvePieveWorkflowToolCallDisplayText from './resolvePieveWorkflowToolCallDisplayText.js';
import { CLAUDE_CODE_TOOL_DISPLAY } from '../pieve/toPieveEquivalentToolCall.js';
import { getPieveKnowledgeLabel } from '../pieve/pieveKnowledgeLabels.js';

const GENERIC_TOOL_CALL_LABEL = 'Working on your request';

const TOOL_NAME_ALIASES = Object.freeze({
  desktop_apply_patch: 'patch_apply',
  desktop_write_file: 'files_write'
});

const PIEVE_TOOL_CALL_DISPLAY_TEXT_MAP = Object.freeze({
  pieve_mcp_apps_search: 'Finding the right app',
  pieve_mcp_apps_get: 'Checking what the app can do',
  pieve_mcp_actions_search: 'Finding the right step in the app',
  pieve_mcp_lookup_resolve: 'Checking the options the app accepts',
  pieve_mcp_action_schema: 'Checking what the app needs',
  pieve_mcp_connections_setup: 'Preparing the app connection',
  pieve_mcp_result_query: 'Refining the results',
  web_search: 'Searching the web',
  read_web_page: 'Reading a web page',
  knowledge_list: 'Checking your notes',
  knowledge_read: 'Reading your notes',
  knowledge_write: 'Saving to your notes',
  knowledge_stat: 'Checking your notes',
  knowledge_move: 'Moving a note',
  knowledge_delete: 'Deleting a note',
  knowledge_search_query: 'Searching your notes',
  knowledge_share: 'Sharing a skill',
  files_list: 'Looking through files',
  files_search: 'Looking for files',
  files_preview: 'Looking at files',
  files_mkdir: 'Creating a folder',
  files_read: 'Reading a file',
  files_write: 'Updating a file',
  files_stat: 'Checking file details',
  files_move: 'Moving a file',
  files_delete: 'Deleting a file',
  patch_apply: 'Updating a file',
  execute_virtual_env: 'Running a calculation',
  exec_command: 'Running a command on your computer',
  desktop_exec_command: 'Running a command on your computer',
  shell_run: 'Running a command on your computer',
  create_subagent: 'Starting a helper for part of the work',
  await_subagents: 'Waiting for the helpers to finish',
  tool_result_read: 'Reading the rest of the results',
  checklist_update: 'Updating the plan',
  schedule_update: 'Saving the schedule',
  trigger_update: 'Saving the trigger',
  create_item: 'Starting a new conversation',
  message_item: 'Sending a message',
  check_item: 'Checking progress',
  linear_issue_state_update: 'Updating the delivery status'
});
const TEXT_WRITE_TOOL_NAMES = Object.freeze(new Set(['files_write', 'patch_apply']));
const FILE_INFO_TOOL_NAMES = Object.freeze(new Set(['files_read', 'files_write', 'files_stat', 'files_move', 'files_delete', 'patch_apply']));

const toLimitedText = (value, maxLength = 48) => {
  const normalized = toSafeString(value);

  if (!normalized) {
    return null;
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength)}...`;
};

const resolveDisplayIntentCandidate = (args = {}) => {
  const safeArgs = args && typeof args === 'object' ? args : {};
  const parsedArgs = safeArgs?.parsedArgs && typeof safeArgs.parsedArgs === 'object' && !Array.isArray(safeArgs.parsedArgs) ? safeArgs.parsedArgs : null;
  const request = safeArgs?.request && typeof safeArgs.request === 'object' && !Array.isArray(safeArgs.request) ? safeArgs.request : null;
  const requestParsedArgs = request?.parsedArgs && typeof request.parsedArgs === 'object' && !Array.isArray(request.parsedArgs) ? request.parsedArgs : null;

  return safeArgs?.display_intent || safeArgs?.displayIntent || parsedArgs?.display_intent || parsedArgs?.displayIntent || request?.display_intent || request?.displayIntent || requestParsedArgs?.display_intent || requestParsedArgs?.displayIntent || null;
};

const isCompletedToolCallArgs = (args = {}) => {
  const safeArgs = args && typeof args === 'object' ? args : {};

  if (safeArgs?.completed === true) {
    return true;
  }

  return isResultLikeValue(safeArgs?.response) || isResultLikeValue(safeArgs?.output) || isResultLikeValue(safeArgs?.result);
};

const toFileName = (value) => {
  const normalized = toSafeString(value);

  if (!normalized) {
    return null;
  }

  const normalizedPath = normalized.replace(/\\/g, '/');
  const segments = normalizedPath.split('/').filter(Boolean);
  const leaf = segments.length > 0 ? segments[segments.length - 1] : normalized;

  return toLimitedText(leaf, 64) || null;
};

const toNormalizedPath = (value) => {
  return toSafeString(value).replace(/\\/g, '/').trim();
};

const toCanonicalToolName = (toolName) => {
  const normalized = toSafeString(toolName).toLowerCase();

  return TOOL_NAME_ALIASES[normalized] || normalized;
};

const toNormalizedPatchTextForParsing = (patchText) => {
  const normalized = toSafeString(patchText).replace(/\r\n?/g, '\n');

  if (!normalized) {
    return '';
  }

  return normalized
    .replace(/\s(\*\*\*\s+(?:Begin Patch|End Patch|(?:Update|Delete|Add)\s+File:))/g, '\n$1')
    .replace(/\s(@@)/g, '\n$1')
    .trim();
};

const toPatchFileName = (patchText) => {
  const normalized = toNormalizedPatchTextForParsing(patchText);

  if (!normalized) {
    return null;
  }

  const patchMatch = normalized.match(/^\*\*\*\s+(?:Update|Delete|Add)\s+File:\s+(.+)$/m);

  if (patchMatch) {
    return toFileName(patchMatch[1]);
  }

  const plusMatch = normalized.match(/^\+\+\+\s+(?:b\/)?(.+)$/m);

  if (plusMatch) {
    return toFileName(plusMatch[1]);
  }

  const minusMatch = normalized.match(/^---\s+(?:a\/)?(.+)$/m);

  if (minusMatch) {
    return toFileName(minusMatch[1]);
  }

  return null;
};

const toPatchFilePath = (patchText) => {
  const normalized = toNormalizedPatchTextForParsing(patchText);

  if (!normalized) {
    return null;
  }

  const patchMatch = normalized.match(/^\*\*\*\s+(?:Update|Delete|Add)\s+File:\s+(.+)$/m);

  if (patchMatch) {
    return toSafeString(patchMatch[1]) || null;
  }

  const plusMatch = normalized.match(/^\+\+\+\s+(?:b\/)?(.+)$/m);

  if (plusMatch) {
    return toSafeString(plusMatch[1]) || null;
  }

  const minusMatch = normalized.match(/^---\s+(?:a\/)?(.+)$/m);

  if (minusMatch) {
    return toSafeString(minusMatch[1]) || null;
  }

  return null;
};

const toPatchDiffStats = (patchText) => {
  const rawPatchText = toSafeString(patchText);
  const normalized = toNormalizedPatchTextForParsing(patchText);

  if (!normalized) {
    return { additions: null, deletions: null };
  }

  let additions = 0;
  let deletions = 0;
  let hasCountedLine = false;
  const lines = normalized.split('\n');

  for (const rawLine of lines) {
    const line = String(rawLine || '').trimStart();

    if (!line || line.startsWith('+++') || line.startsWith('---') || line.startsWith('***')) {
      continue;
    }

    if (line.startsWith('+')) {
      additions += 1;
      hasCountedLine = true;
      continue;
    }

    if (line.startsWith('-')) {
      deletions += 1;
      hasCountedLine = true;
    }
  }

  if (hasCountedLine) {
    return { additions, deletions };
  }

  if (rawPatchText && !rawPatchText.includes('\n')) {
    const hasInlineAdditionMarker = /(?:^|\s)\+(?=\S)/.test(rawPatchText);
    const hasInlineDeletionMarker = /(?:^|\s)-(?=\S)/.test(rawPatchText);

    if (hasInlineAdditionMarker || hasInlineDeletionMarker) {
      return {
        additions: hasInlineAdditionMarker ? 1 : 0,
        deletions: hasInlineDeletionMarker ? 1 : 0
      };
    }
  }

  const patchBody = normalized
    .replace(/\*\*\*\s+Begin Patch/gi, '')
    .replace(/\*\*\*\s+End Patch/gi, '')
    .replace(/\*\*\*\s+(?:Update|Delete|Add)\s+File:\s+[^\n]+/gi, '')
    .trim();
  const tokens = patchBody.split(/\s+/).filter(Boolean);

  for (const token of tokens) {
    if (token.startsWith('+++') || token.startsWith('---')) {
      continue;
    }

    if (token === '+' || token === '-') {
      continue;
    }

    if (/^[+-]\d+(?:,\d+)?$/.test(token)) {
      continue;
    }

    if (token.startsWith('+')) {
      additions += 1;
      hasCountedLine = true;
      continue;
    }

    if (token.startsWith('-')) {
      deletions += 1;
      hasCountedLine = true;
    }
  }

  if (!hasCountedLine) {
    return { additions: null, deletions: null };
  }

  return { additions, deletions };
};

const toNonNegativeIntegerOrNull = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  if (parsed < 0) {
    return null;
  }

  return Math.floor(parsed);
};

const toBooleanOrNull = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  return null;
};

const toSummaryFiles = (args = {}) => {
  const safeArgs = args && typeof args === 'object' ? args : {};
  const candidates = [safeArgs?.response?.output?.data?.summary?.files, safeArgs?.response?.data?.summary?.files, safeArgs?.output?.data?.summary?.files, safeArgs?.result?.data?.summary?.files, safeArgs?.data?.summary?.files, safeArgs?.summary?.files];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return [];
};

const toSingleSummaryFilePath = (summaryFiles = []) => {
  if (!Array.isArray(summaryFiles) || summaryFiles.length !== 1) {
    return null;
  }

  return toSafeString(summaryFiles[0]?.path) || null;
};

const getDiffStatsForFilePath = ({ summaryFiles = [], filePath = '' } = {}) => {
  const normalizedFilePath = toSafeString(filePath).replace(/\\/g, '/');
  const normalizedLeaf = toFileName(normalizedFilePath);

  if (!Array.isArray(summaryFiles) || summaryFiles.length < 1) {
    return { additions: null, deletions: null, selected: null };
  }

  const normalizeCandidatePath = (value) => {
    return toSafeString(value).replace(/\\/g, '/');
  };

  const matchedByPath = summaryFiles.find((entry) => {
    const candidatePath = normalizeCandidatePath(entry?.path);
    return Boolean(normalizedFilePath && candidatePath && candidatePath === normalizedFilePath);
  });
  const matchedByLeaf = matchedByPath
    ? null
    : summaryFiles.find((entry) => {
        const candidateLeaf = toFileName(entry?.path);
        return Boolean(normalizedLeaf && candidateLeaf && candidateLeaf === normalizedLeaf);
      });
  const selected = matchedByPath || matchedByLeaf || null;

  return {
    additions: toNonNegativeIntegerOrNull(selected?.additions),
    deletions: toNonNegativeIntegerOrNull(selected?.deletions),
    selected
  };
};

const isZeroDiffStats = ({ additions, deletions } = {}) => {
  return additions === 0 && deletions === 0;
};

const resolveWebSearchTarget = (args = {}) => {
  const payload = args && typeof args === 'object' ? args : {};
  const directUrl = toLimitedText(payload?.url || payload?.webUrl || payload?.targetUrl || payload?.sourceUrl, 96);

  if (directUrl) {
    return directUrl;
  }

  const searchQueryItems = Array.isArray(payload?.search_query) ? payload.search_query : [];

  if (searchQueryItems.length > 0) {
    const firstSearchQuery = searchQueryItems[0] || {};
    const firstSearchQueryUrl = toLimitedText(firstSearchQuery?.url || firstSearchQuery?.webUrl || firstSearchQuery?.targetUrl, 96);

    if (firstSearchQueryUrl) {
      return firstSearchQueryUrl;
    }

    const firstSearchQueryText = toLimitedText(firstSearchQuery?.q, 96);

    if (firstSearchQueryText) {
      return firstSearchQueryText;
    }
  }

  const directQuery = toLimitedText(payload?.query || payload?.q, 96);

  if (directQuery) {
    return directQuery;
  }

  return null;
};

const resolveMcpDisplayText = ({ normalizedToolName, args }) => {
  const sharedMcpDisplayText = resolvePieveMcpToolCallDisplayText({
    toolName: normalizedToolName,
    args
  });

  if (sharedMcpDisplayText) {
    return sharedMcpDisplayText;
  }

  if (normalizedToolName === 'web_search') {
    const target = resolveWebSearchTarget(args);
    const label = target ? `Searching the web for ${target}` : 'Searching the web';

    return args?.completed ? toPastTenseDisplayLabel(label) : label;
  }

  return null;
};

/* The label of a tool with no label of its own: a person sees what Pieve is doing for them, never a tool name. */
const toFallbackToolLabel = () => {
  return GENERIC_TOOL_CALL_LABEL;
};

const finalizeToolCallDisplayLabel = ({ label, completed = false } = {}) => {
  const normalized = toSafeString(label);

  if (!normalized) {
    return '';
  }

  return completed ? toPastTenseDisplayLabel(normalized) : normalized;
};

const getPieveToolCallFileInfo = ({ toolName, args } = {}) => {
  const normalizedToolName = toCanonicalToolName(toolName);
  const safeArgs = args && typeof args === 'object' ? args : {};
  const summaryFiles = toSummaryFiles(safeArgs);
  const responseSummaryFilePath = toSingleSummaryFilePath(summaryFiles);
  const parsedArgs = safeArgs?.parsedArgs || safeArgs?.request?.parsedArgs;
  const patchText = safeArgs?.patchText || safeArgs?.parsedArgs?.patchText || safeArgs?.request?.patchText || safeArgs?.request?.parsedArgs?.patchText;
  const patchDiffStats = normalizedToolName === 'patch_apply' ? toPatchDiffStats(patchText) : { additions: null, deletions: null };
  const patchPathFromPayload = normalizedToolName === 'patch_apply' ? toPatchFilePath(patchText) : null;
  const patchPathFromArgs = toSafeString(safeArgs?.patchPath || safeArgs?.patchFilePath || parsedArgs?.patchPath || parsedArgs?.patchFilePath || safeArgs?.request?.patchPath || safeArgs?.request?.parsedArgs?.patchPath || safeArgs?.request?.parsedArgs?.patchFilePath);
  const patchAdditions = toNonNegativeIntegerOrNull(safeArgs?.patchAdditions ?? parsedArgs?.patchAdditions ?? safeArgs?.request?.patchAdditions ?? safeArgs?.request?.parsedArgs?.patchAdditions);
  const patchDeletions = toNonNegativeIntegerOrNull(safeArgs?.patchDeletions ?? parsedArgs?.patchDeletions ?? safeArgs?.request?.patchDeletions ?? safeArgs?.request?.parsedArgs?.patchDeletions);
  const parsedSourcePath = parsedArgs?.sourcePath || parsedArgs?.fromPath;
  const parsedTargetPath = parsedArgs?.targetPath || parsedArgs?.toPath || parsedArgs?.destinationPath;
  const sourcePath = toNormalizedPath(safeArgs?.sourcePath || safeArgs?.fromPath || parsedSourcePath);
  const targetPath = toNormalizedPath(safeArgs?.targetPath || safeArgs?.toPath || safeArgs?.destinationPath || responseSummaryFilePath || patchPathFromArgs || patchPathFromPayload);
  const parsedFilePath = parsedArgs?.filePath || parsedArgs?.path || parsedArgs?.file || parsedTargetPath || parsedSourcePath;
  const filePath = toNormalizedPath(safeArgs?.filePath || safeArgs?.path || safeArgs?.file || safeArgs?.targetPath || safeArgs?.sourcePath || parsedFilePath || responseSummaryFilePath || patchPathFromArgs || patchPathFromPayload);

  const fileName = toFileName(filePath);
  const sourceFileName = toFileName(sourcePath);
  const targetFileName = toFileName(targetPath);
  const didRename = Boolean(normalizedToolName === 'files_move' && sourceFileName && targetFileName && sourceFileName !== targetFileName);

  if (FILE_INFO_TOOL_NAMES.has(normalizedToolName)) {
    if (fileName && filePath) {
      const { additions, deletions, selected } = getDiffStatsForFilePath({
        summaryFiles,
        filePath
      });
      const selectedCreated = toBooleanOrNull(selected?.created);
      const payloadCreated = toBooleanOrNull(safeArgs?.created ?? safeArgs?.response?.output?.data?.created ?? safeArgs?.response?.data?.created ?? safeArgs?.output?.data?.created ?? safeArgs?.result?.data?.created ?? safeArgs?.data?.created ?? parsedArgs?.created ?? safeArgs?.request?.parsedArgs?.created);
      const isCreated = selectedCreated ?? payloadCreated;

      if (normalizedToolName === 'patch_apply') {
        return {
          fileName,
          filePath,
          additions: additions ?? patchAdditions ?? patchDiffStats.additions,
          deletions: deletions ?? patchDeletions ?? patchDiffStats.deletions,
          isCreated,
          sourceFileName,
          sourcePath,
          targetFileName,
          targetPath,
          didRename
        };
      }

      return { fileName, filePath, additions, deletions, isCreated, sourceFileName, sourcePath, targetFileName, targetPath, didRename };
    }
  }

  return { fileName: null, filePath: null, additions: null, deletions: null, isCreated: null, sourceFileName: null, sourcePath: null, targetFileName: null, targetPath: null, didRename: false };
};

const getPieveToolCallDisplayText = ({ toolName, args } = {}) => {
  const normalizedToolName = toCanonicalToolName(toolName);
  const safeArgs = args && typeof args === 'object' ? args : {};
  const completed = isCompletedToolCallArgs(safeArgs);
  const summaryFiles = toSummaryFiles(safeArgs);
  const responseSummaryFilePath = toSingleSummaryFilePath(summaryFiles);
  const parsedArgs = safeArgs?.parsedArgs || safeArgs?.request?.parsedArgs;
  const parsedFilePath = parsedArgs?.filePath || parsedArgs?.path || parsedArgs?.file || parsedArgs?.targetPath || parsedArgs?.sourcePath;
  const displayIntentText = resolvePieveToolCallDisplayIntentText({
    displayIntent: resolveDisplayIntentCandidate(safeArgs),
    completed
  });

  if (!normalizedToolName) {
    return GENERIC_TOOL_CALL_LABEL;
  }

  if (displayIntentText) {
    return displayIntentText;
  }

  // A plan run's Claude Code step that Pieve has no tool for keeps Claude Code's name (toPieveEquivalentToolCall).
  const claudeCodeLabels = Object.hasOwn(CLAUDE_CODE_TOOL_DISPLAY, toSafeString(toolName)) ? CLAUDE_CODE_TOOL_DISPLAY[toSafeString(toolName)].labels : null;

  if (claudeCodeLabels) {
    return completed ? claudeCodeLabels.completed : claudeCodeLabels.running;
  }

  const fileName = toFileName(safeArgs?.filePath || safeArgs?.path || safeArgs?.file || safeArgs?.targetPath || safeArgs?.sourcePath || parsedFilePath || responseSummaryFilePath);
  const { additions, deletions, isCreated, sourceFileName, targetFileName, didRename } = getPieveToolCallFileInfo({
    toolName: normalizedToolName,
    args: safeArgs
  });
  const shouldRenderCreatedFileLabel = TEXT_WRITE_TOOL_NAMES.has(normalizedToolName) && (isCreated === true || isZeroDiffStats({ additions, deletions }));

  if (normalizedToolName === 'files_read') {
    return finalizeToolCallDisplayLabel({
      label: fileName ? `Reading ${fileName}` : 'Reading a file',
      completed
    });
  }

  if (normalizedToolName === 'files_write') {
    return finalizeToolCallDisplayLabel({
      label: fileName && shouldRenderCreatedFileLabel ? `Created ${fileName}` : fileName ? `Updating ${fileName}` : 'Updating a file',
      completed
    });
  }

  if (normalizedToolName === 'patch_apply') {
    const patchText = safeArgs?.patchText || safeArgs?.parsedArgs?.patchText || safeArgs?.request?.patchText || safeArgs?.request?.parsedArgs?.patchText;
    const patchPathFromArgs = safeArgs?.patchPath || safeArgs?.patchFilePath || safeArgs?.parsedArgs?.patchPath || safeArgs?.parsedArgs?.patchFilePath || safeArgs?.request?.patchPath || safeArgs?.request?.parsedArgs?.patchPath || safeArgs?.request?.parsedArgs?.patchFilePath;
    const patchFileName = toPatchFileName(patchText) || toFileName(patchPathFromArgs) || toFileName(responseSummaryFilePath) || fileName;

    return finalizeToolCallDisplayLabel({
      label: patchFileName && shouldRenderCreatedFileLabel ? `Created ${patchFileName}` : patchFileName ? `Updating file ${patchFileName}` : 'Updating a file',
      completed
    });
  }

  if (normalizedToolName === 'files_stat') {
    return finalizeToolCallDisplayLabel({
      label: fileName ? `Checking ${fileName} details` : 'Checking file details',
      completed
    });
  }

  if (normalizedToolName === 'files_move') {
    return finalizeToolCallDisplayLabel({
      label: didRename && targetFileName ? `Renamed file to ${targetFileName}` : targetFileName ? `Moved file ${targetFileName}` : sourceFileName ? `Moved file ${sourceFileName}` : fileName ? `Moving ${fileName}` : 'Moving a file',
      completed
    });
  }

  if (normalizedToolName === 'files_delete') {
    return finalizeToolCallDisplayLabel({
      label: fileName ? `Deleting ${fileName}` : 'Deleting a file',
      completed
    });
  }

  if (normalizedToolName === 'search_ripgrep') {
    const queryText = toLimitedText(safeArgs?.query || safeArgs?.q, 96);

    return finalizeToolCallDisplayLabel({
      label: queryText ? `Searching files for "${queryText}"` : 'Searching files',
      completed
    });
  }

  const mcpText = resolveMcpDisplayText({ normalizedToolName, args: safeArgs });

  if (mcpText) {
    return finalizeToolCallDisplayLabel({
      label: mcpText,
      completed
    });
  }

  /* A built-in guide is named by its topic ("Reading about schedules"), never by its path. */
  const builtInKnowledgeLabel = normalizedToolName === 'knowledge_read' ? getPieveKnowledgeLabel(toSafeString(safeArgs?.path || parsedArgs?.path).replace(/^\/+/, '')) : null;

  if (builtInKnowledgeLabel) {
    return finalizeToolCallDisplayLabel({
      label: `Reading about ${builtInKnowledgeLabel}`,
      completed
    });
  }

  const knowledgeText = resolvePieveKnowledgeToolCallDisplayText({
    toolName: normalizedToolName,
    args: safeArgs
  });

  if (knowledgeText) {
    return finalizeToolCallDisplayLabel({
      label: knowledgeText,
      completed
    });
  }

  const workflowText = resolvePieveWorkflowToolCallDisplayText({ toolName: normalizedToolName, args: safeArgs, completed });

  if (workflowText) {
    return workflowText;
  }

  const mapped = PIEVE_TOOL_CALL_DISPLAY_TEXT_MAP[normalizedToolName];

  if (mapped) {
    return finalizeToolCallDisplayLabel({
      label: mapped,
      completed
    });
  }

  const fallbackLabel = toFallbackToolLabel(normalizedToolName);

  return finalizeToolCallDisplayLabel({
    label: fallbackLabel,
    completed
  });
};

export default getPieveToolCallDisplayText;
export { PIEVE_TOOL_CALL_DISPLAY_TEXT_MAP, getPieveToolCallFileInfo, toFallbackToolLabel };
